import { Controller } from '@hotwired/stimulus'

// Day map on the week show page. Clicking a day-of-week map icon fetches the
// day's jobs (with geocoded coordinates cached on the customer) and plots
// them on a Leaflet map, colour-coded by worklist (grey = unassigned). Each
// marker shows its time block as a permanent label, and clicking it opens a
// popup with the job details and an editable worklist.
export default class extends Controller {
  static targets = ['panel', 'map', 'title', 'status', 'routeControl', 'routeButtons']
  static values = { weekId: Number, apiKey: String }

  connect() {
    this.openDay = null
    this.map = null
    this.markerLayer = null
    this.markerByJobId = new Map()
    this.currentJobs = []
    this.workListOptions = []
    this.canEditWorklist = false
    this.routeLayer = null
    this.jobsByWorklist = new Map()
    this.worklists = []
    this.selectedRouteWorklistId = null
    this.routeRequestId = 0
  }

  async open(event) {
    const button = event.currentTarget
    const day = parseInt(button.dataset.day, 10)
    const label = button.dataset.dayLabel || `Day ${day}`

    if (this.openDay === day) {
      this.close()
      return
    }

    this.openDay = day
    this.titleTarget.textContent = `Jobs — ${label}`
    this.panelTarget.classList.remove('hidden')
    this.ensureMap()
    if (this.map) this.map.invalidateSize()

    this.setStatus('Loading…')
    const jobs = await this.fetchJobs(day)
    if (this.openDay !== day) return
    this.renderJobs(jobs)
  }

  close() {
    this.openDay = null
    this.panelTarget.classList.add('hidden')
  }

  async fetchJobs(day) {
    const url = `/weeks/${this.weekIdValue}/map_day.json?day=${day}`
    const response = await fetch(url, { headers: { Accept: 'application/json' } })
    if (!response.ok) throw new Error(`HTTP ${response.status}`)
    const data = await response.json()
    this.workListOptions = data.work_list_options || []
    this.canEditWorklist = !!data.can_edit_worklist
    return data.jobs || []
  }

  ensureMap() {
    if (this.map) return
    if (typeof L === 'undefined') return

    this.map = L.map(this.mapTarget, { scrollWheelZoom: true }).setView([51.5287, -0.1405], 12)

    // LocationIQ "light" is the non-colourful basemap used across the app and
    // has full-resolution tiles. Its `pk.` key is public (safe for client-side
    // use); the server supplies it via ENV, with a default here so local dev
    // still gets the proper tiles when the env var isn't set.
    const apiKey = this.apiKeyValue || 'pk.30a102ba8615c6cb13ebdddcc330e863'
    const tileUrl = `https://{s}-tiles.locationiq.com/v3/light/r/{z}/{x}/{y}.png?key=${encodeURIComponent(apiKey)}`
    const attribution = '&copy; LocationIQ'

    L.tileLayer(tileUrl, { maxZoom: 19, attribution }).addTo(this.map)
    this.routeLayer = L.featureGroup().addTo(this.map)
    this.markerLayer = L.featureGroup().addTo(this.map)
  }

  renderJobs(jobs) {
    if (!this.map || !this.markerLayer) return
    this.markerLayer.clearLayers()
    if (this.routeLayer) this.routeLayer.clearLayers()
    this.markerByJobId = new Map()
    this.currentJobs = jobs

    jobs.forEach((job) => {
      if (job.latitude == null || job.longitude == null) return
      const marker = this.createMarker(job)
      this.markerLayer.addLayer(marker)
      this.markerByJobId.set(job.job_id, marker)
    })

    this.updateRoutes()

    if (this.markerByJobId.size > 0) {
      this.setStatus('')
    } else {
      this.setStatus(jobs.length
        ? 'Jobs are still being geocoded — close and reopen in a moment.'
        : 'No jobs for this day.')
    }
  }

  // Group geocoded, worklist-assigned jobs by worklist. The server sorts jobs
  // by worklist order, so the order within each group matches the worklist.
  groupJobsByWorklist() {
    const grouped = new Map()
    this.currentJobs.forEach((job) => {
      if (job.latitude == null || job.longitude == null) return
      if (job.work_list_id == null) return
      if (!grouped.has(job.work_list_id)) grouped.set(job.work_list_id, [])
      grouped.get(job.work_list_id).push(job)
    })
    return grouped
  }

  // Rebuild the worklist buttons, pick the initial selection, then redraw the
  // route, update marker visibility and refit the viewport.
  updateRoutes() {
    this.jobsByWorklist = this.groupJobsByWorklist()
    this.buildWorklistButtons()
    this.applySelection()
  }

  buildWorklistButtons() {
    const buttons = this.routeButtonsTarget
    const control = this.routeControlTarget
    buttons.innerHTML = ''

    this.worklists = Array.from(this.jobsByWorklist.entries()).map(([id, jobs]) => ({
      id,
      name: jobs[0].work_list_name || `Worklist #${id}`,
      color: jobs[0].color || '#94a3b8'
    }))

    this.worklists.forEach((wl) => {
      const button = document.createElement('button')
      button.type = 'button'
      button.className = 'day-map-route-button'
      button.textContent = wl.name
      button.dataset.worklistId = String(wl.id)
      button.addEventListener('click', () => this.toggleRoute(wl.id))
      buttons.appendChild(button)
    })

    // A single worklist is selected automatically (route shown straight away);
    // with several, start in the "show all" state until a button is clicked.
    this.selectedRouteWorklistId = this.worklists.length === 1 ? this.worklists[0].id : null
    control.classList.toggle('hidden', this.worklists.length === 0)
  }

  toggleRoute(worklistId) {
    this.selectedRouteWorklistId = this.selectedRouteWorklistId === worklistId ? null : worklistId
    this.applySelection()
  }

  applySelection() {
    if (this.selectedRouteWorklistId != null && !this.jobsByWorklist.has(this.selectedRouteWorklistId)) {
      this.selectedRouteWorklistId = null
    }
    this.highlightSelectedButton()
    this.drawRoute(this.selectedRouteWorklistId)
    this.setMarkerVisibility()
    this.fitToVisible()
  }

  highlightSelectedButton() {
    const selected = this.selectedRouteWorklistId
    this.routeButtonsTarget.querySelectorAll('.day-map-route-button').forEach((button) => {
      const id = Number(button.dataset.worklistId)
      const active = selected != null && id === selected
      button.classList.toggle('day-map-route-button--active', active)
      if (active) {
        const wl = this.worklists.find((w) => w.id === id)
        button.style.setProperty('--route-color', wl ? wl.color : '#94a3b8')
      } else {
        button.style.removeProperty('--route-color')
      }
    })
  }

  // When a route is shown, hide the markers of every other job — including
  // unassigned jobs — so only the selected worklist remains on the map.
  setMarkerVisibility() {
    const selected = this.selectedRouteWorklistId
    this.currentJobs.forEach((job) => {
      const marker = this.markerByJobId.get(job.job_id)
      if (!marker) return
      const visible = selected == null || job.work_list_id === selected
      if (visible) {
        if (!this.markerLayer.hasLayer(marker)) this.markerLayer.addLayer(marker)
      } else {
        if (this.markerLayer.hasLayer(marker)) this.markerLayer.removeLayer(marker)
      }
    })
  }

  fitToVisible() {
    if (!this.map) return
    const bounds = L.latLngBounds([])
    this.currentJobs.forEach((job) => {
      const marker = this.markerByJobId.get(job.job_id)
      if (marker && this.markerLayer.hasLayer(marker)) {
        bounds.extend(marker.getLatLng())
      }
    })
    if (bounds.isValid()) {
      this.map.fitBounds(bounds, { padding: [30, 30], maxZoom: 16 })
    }
  }

  async drawRoute(worklistId) {
    if (!this.routeLayer) return
    this.routeLayer.clearLayers()

    if (worklistId == null) return

    const jobs = this.jobsByWorklist.get(worklistId)
    if (!jobs || jobs.length < 2) return

    const color = jobs[0].color || '#94a3b8'
    const straightLatlngs = jobs.map((job) => [job.latitude, job.longitude])

    const draw = (latlngs) => {
      this.routeLayer.clearLayers()
      L.polyline(latlngs, {
        color,
        weight: 4,
        opacity: 0.75,
        dashArray: '8 8',
        lineJoin: 'round'
      }).addTo(this.routeLayer)
    }

    // Draw a provisional straight line, then replace it with the road-following
    // geometry once the routing endpoint responds.
    draw(straightLatlngs)

    const token = (this.routeRequestId = (this.routeRequestId || 0) + 1)
    const road = await this.fetchRoute(worklistId)
    if (token !== this.routeRequestId) return
    if (this.selectedRouteWorklistId !== worklistId) return
    if (road && road.length >= 2) draw(road)
  }

  // Fetch a road-following route from the server (LocationIQ Directions).
  // Returns null on failure so the caller keeps the straight-line polyline.
  async fetchRoute(worklistId) {
    try {
      const url = `/weeks/${this.weekIdValue}/map_route.json?work_list_id=${worklistId}`
      const response = await fetch(url, { headers: { Accept: 'application/json' } })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      return Array.isArray(data.geometry) ? data.geometry : null
    } catch (error) {
      return null
    }
  }

  createMarker(job) {
    const marker = L.marker([job.latitude, job.longitude], { icon: this.markerIcon(job) })
    marker.bindPopup(() => this.buildPopup(job), { minWidth: 230 })
    return marker
  }

  // Worklist-coloured pin showing the job's time block. The tail points
  // exactly at the house, so it is clear which property the time belongs to.
  markerIcon(job) {
    const time = job.time_label ? this.escapeHtml(job.time_label) : 'No time'
    const color = job.color
    return L.divIcon({
      className: 'day-map-marker',
      html: `<span class="day-map-marker-pin" style="--marker-color:${color}">${time}</span>`,
      iconSize: [0, 0],
      iconAnchor: [0, 0],
      popupAnchor: [0, -24]
    })
  }

  buildPopup(job) {
    const container = document.createElement('div')
    container.className = 'day-map-popup'

    const heading = document.createElement('strong')
    heading.className = 'day-map-popup-address'
    heading.textContent = job.address || 'No address'
    container.appendChild(heading)

    if (job.invoice_number) {
      const invoice = document.createElement('div')
      invoice.className = 'day-map-popup-invoice'
      invoice.textContent = `Job #${job.invoice_number}`
      container.appendChild(invoice)
    }

    this.appendPopupRow(container, 'Time', job.time_label || '—')
    this.appendPopupRow(container, 'Price', job.price || '—')

    if (this.canEditWorklist) {
      const row = document.createElement('div')
      row.className = 'day-map-popup-row'
      const label = document.createElement('span')
      label.className = 'day-map-popup-label'
      label.textContent = 'Worklist'
      const select = document.createElement('select')
      select.className = 'day-map-worklist-select'
      select.setAttribute('aria-label', 'Change worklist')

      this.workListOptions.forEach(([name, id]) => {
        const option = document.createElement('option')
        option.value = id == null ? '' : String(id)
        option.textContent = name
        option.selected = String(job.work_list_category_id ?? '') === option.value
        select.appendChild(option)
      })

      select.addEventListener('change', () => this.changeWorklist(job, select))
      row.appendChild(label)
      row.appendChild(select)
      container.appendChild(row)
    } else {
      this.appendPopupRow(container, 'Worklist', job.work_list_name || 'Unassigned')
    }

    return container
  }

  appendPopupRow(container, label, value) {
    const row = document.createElement('div')
    row.className = 'day-map-popup-row'
    const labelEl = document.createElement('span')
    labelEl.className = 'day-map-popup-label'
    labelEl.textContent = label
    const valueEl = document.createElement('span')
    valueEl.className = 'day-map-popup-value'
    valueEl.textContent = value
    row.appendChild(labelEl)
    row.appendChild(valueEl)
    container.appendChild(row)
  }

  async changeWorklist(job, select) {
    const value = select.value
    const token = this.csrfToken()
    select.disabled = true

    try {
      const response = await fetch(`/jobs/${job.job_id}`, {
        method: 'PATCH',
        headers: {
          Accept: 'application/json',
          'Content-Type': 'application/x-www-form-urlencoded; charset=UTF-8',
          'X-CSRF-Token': token
        },
        body: new URLSearchParams({ 'job[work_list_category_id]': value })
      })
      if (!response.ok) throw new Error(`HTTP ${response.status}`)
      const data = await response.json()
      if (!data.success) throw new Error('update failed')

      job.work_list_category_id = value === '' ? null : Number(value)
      job.assigned = value !== ''
      job.work_list_name = data.work_list_name
      job.color = data.work_list_color
      job.work_list_id = data.work_list_with_jobs_id ?? null

      const marker = this.markerByJobId.get(job.job_id)
      if (marker) {
        marker.setIcon(this.markerIcon(job))
        marker.setPopupContent(this.buildPopup(job))
      }

      this.updateRoutes()
      this.updateWorklistGrid(data)
    } catch (error) {
      alert('Could not update worklist')
      select.value = String(job.work_list_category_id ?? '')
    } finally {
      select.disabled = false
    }
  }

  csrfToken() {
    return document.querySelector('meta[name="csrf-token"]')?.getAttribute('content') || ''
  }

  // Keep the worklist grid above the map in sync after a job is reassigned:
  // a worklist that just gained its first job turns green, one that lost its
  // last job turns white again.
  updateWorklistGrid(data) {
    const grid = document.querySelector('.work-list-grid')
    if (!grid) return

    if (data.work_list_without_jobs_id != null) {
      const cell = grid.querySelector(`td[data-work-list-id="${data.work_list_without_jobs_id}"]`)
      if (cell) cell.classList.remove('work-list-with-jobs')
    }
    if (data.work_list_with_jobs_id != null) {
      const cell = grid.querySelector(`td[data-work-list-id="${data.work_list_with_jobs_id}"]`)
      if (cell) cell.classList.add('work-list-with-jobs')
    }
  }

  setStatus(message) {
    if (!this.hasStatusTarget) return
    this.statusTarget.textContent = message || ''
    this.statusTarget.classList.toggle('hidden', !message)
  }

  escapeHtml(value) {
    const map = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }
    return String(value == null ? '' : value).replace(/[&<>"']/g, (ch) => map[ch])
  }
}
